import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../AuthContext';
import { useTranslation } from 'react-i18next';

// Конфигурация карточек сообщений
const MESSAGE_CONFIGS = [
  {
    key: 'subscription_expiring_3days',
    icon: '⏰',
    iconBg: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)',
    title: 'Подписка заканчивается',
    subtitle: 'Отправляется за 1–3 дня до окончания платной подписки. В текст подставляются {days} и {days_word} (день/дня/дней).',
    defaultText: '⚡ Ваша подписка истекает через {days} {days_word}!\n\nНе теряйте доступ к защищенному интернету. Продлите подписку прямо сейчас!',
    defaultButton: { text: '🛒 Тарифы', action: 'tariffs' },
  },
  {
    key: 'trial_expiring',
    icon: '🔥',
    iconBg: 'linear-gradient(135deg, #F093FB 0%, #F5576C 100%)',
    title: 'Триал заканчивается',
    subtitle: 'Отправляется когда пробный период подходит к концу',
    defaultText: '🔥 Ваш пробный период заканчивается!\n\nОформите подписку прямо сейчас.',
    defaultButton: { text: '💎 Тарифы', action: 'tariffs' },
  },
  {
    key: 'trial_active',
    icon: '✨',
    iconBg: 'linear-gradient(135deg, #4FACFE 0%, #00F2FE 100%)',
    title: 'Триал активен',
    subtitle: 'Отправляется пользователям с активным пробным периодом',
    defaultText: '🎉 Ваш пробный доступ активен!\n\nНаслаждайтесь защищенным интернетом!',
    defaultButton: { text: '📱 Открыть бота', action: 'webapp' },
  },
  {
    key: 'trial_not_used',
    icon: '🎁',
    iconBg: 'linear-gradient(135deg, #43E97B 0%, #38F9D7 100%)',
    title: 'Триал не использован',
    subtitle: 'Отправляется пользователям, которые не активировали триал',
    defaultText: '🚀 Бесплатная пробная подписка ждёт вас!\n\nАктивируйте триал прямо сейчас! 🔥',
    defaultButton: { text: '🆓 Получить триал', action: 'trial' },
  },
  {
    key: 'no_subscription',
    icon: '💤',
    iconBg: 'linear-gradient(135deg, #667EEA 0%, #764BA2 100%)',
    title: 'Нет подписки',
    subtitle: 'Отправляется пользователям без активной подписки',
    defaultText: '🔔 Вы ещё не оформили VPN?\n\nПодключитесь сейчас и защитите свой трафик!',
    defaultButton: { text: '🛒 Тарифы', action: 'tariffs' },
  },
];

function AdminBroadcastPage() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('manual');
  const [autoMessages, setAutoMessages] = useState({});
  const [loadingAuto, setLoadingAuto] = useState(false);
  const [savingAuto, setSavingAuto] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [recipientType, setRecipientType] = useState('all');
  const [customEmails, setCustomEmails] = useState('');
  const [broadcastType, setBroadcastType] = useState('email');
  const [botType, setBotType] = useState('old');
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [pinMessage, setPinMessage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [usersEmails, setUsersEmails] = useState([]);
  
  const [schedulerSettings, setSchedulerSettings] = useState({ enabled: true, hours: '9,14,19' });
  const [savingScheduler, setSavingScheduler] = useState(false);

  useEffect(() => {
    const fetchEmails = async () => {
      try {
        const response = await fetch('/api/admin/users/emails', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setUsersEmails(data);
        }
      } catch (e) {
        console.error('Ошибка загрузки email:', e);
      }
    };
    
    if (token) {
      fetchEmails();
    }
  }, [token]);

  useEffect(() => {
    const fetchAutoMessages = async () => {
      if (activeTab !== 'auto') return;
      
      setLoadingAuto(true);
      try {
        const response = await fetch('/api/admin/auto-broadcast-messages', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setAutoMessages(data);
        }
        
        const settingsResponse = await fetch('/api/admin/auto-broadcast-settings', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (settingsResponse.ok) {
          const settingsData = await settingsResponse.json();
          setSchedulerSettings(settingsData);
        }
      } catch (e) {
        console.error('Ошибка загрузки:', e);
      } finally {
        setLoadingAuto(false);
      }
    };
    
    if (token && activeTab === 'auto') {
      fetchAutoMessages();
    }
  }, [token, activeTab]);
  
  const handleSaveSchedulerSettings = async () => {
    setSavingScheduler(true);
    try {
      const response = await fetch('/api/admin/auto-broadcast-settings', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(schedulerSettings)
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Ошибка сохранения');
      }
      
      alert('Настройки планировщика сохранены!');
    } catch (e) {
      alert('Ошибка: ' + e.message);
    } finally {
      setSavingScheduler(false);
    }
  };

  const handleSaveAutoMessages = async () => {
    setSavingAuto(true);
    try {
      const response = await fetch('/api/admin/auto-broadcast-messages', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ messages: autoMessages })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Ошибка сохранения');
      }

      alert('Настройки автоматических рассылок сохранены!');
    } catch (e) {
      alert('Ошибка: ' + e.message);
    } finally {
      setSavingAuto(false);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('Размер изображения не должен превышать 10MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        setError('Выберите файл изображения');
        return;
      }
      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      let recipients = [];
      if (recipientType === 'custom') {
        const emails = customEmails.split('\n')
          .map(email => email.trim())
          .filter(email => email && email.includes('@'));
        recipients = emails;
      }

      const formData = new FormData();
      formData.append('subject', subject);
      formData.append('message', message);
      formData.append('recipient_type', recipientType);
      formData.append('custom_emails', JSON.stringify(recipientType === 'custom' ? recipients : []));
      formData.append('broadcast_type', broadcastType);
      formData.append('bot_type', botType);
      formData.append('pin_message', pinMessage);
      
      if (photo) {
        formData.append('photo', photo);
      }

      const response = await fetch('/api/admin/broadcast', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        throw new Error(`Ошибка сервера: ${text.substring(0, 200)}`);
      }
      
      if (!response.ok) {
        throw new Error(data.message || data.error || 'Ошибка отправки рассылки');
      }

      setSuccess({
        total: data.total_recipients,
        broadcast_type: data.broadcast_type,
        bot_type: data.bot_type,
        email: data.email,
        telegram: data.telegram
      });
      
      setSubject('');
      setMessage('');
      setCustomEmails('');
      setPhoto(null);
      setPhotoPreview(null);
      setPinMessage(false);
      
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Обновление поля автосообщения
  const handleFieldChange = useCallback((msgKey, field, value) => {
    setAutoMessages(prev => ({
      ...prev,
      [msgKey]: {
        ...prev[msgKey],
        [field]: value
      }
    }));
  }, []);

  // Переключение включения автосообщения
  const handleToggle = useCallback((msgKey, config) => {
    setAutoMessages(prev => {
      const current = prev[msgKey] || {};
      const currentEnabled = current.enabled !== false;
      return {
        ...prev,
        [msgKey]: {
          ...current,
          enabled: !currentEnabled,
          message_text: current.message_text || config.defaultText,
          bot_type: current.bot_type || 'both',
          button_text: current.button_text || config.defaultButton?.text || '',
          button_action: current.button_action || config.defaultButton?.action || '',
          button_url: current.button_url || '',
        }
      };
    });
  }, []);

  return (
    <main className="admin-page-content">
      <section>
        <h2 className="admin-page-title">{t('broadcast.title')}</h2>
        <p className="admin-description-text">{t('broadcast.description')}</p>

        {/* Табы */}
        <div style={{ 
          marginBottom: '24px',
          display: 'flex',
          gap: '8px',
          background: 'var(--surface-muted)',
          padding: '6px',
          borderRadius: '12px',
          width: 'fit-content',
        }}>
          <button
            onClick={() => setActiveTab('manual')}
            style={{
              padding: '10px 20px',
              background: activeTab === 'manual' ? 'var(--primary)' : 'transparent',
              color: activeTab === 'manual' ? 'white' : 'var(--text-secondary)',
              border: 'none',
              cursor: 'pointer',
              borderRadius: '8px',
              fontWeight: '600',
              fontSize: '14px',
            }}
          >
            📤 {t('broadcast.tab_manual')}
          </button>
          <button
            onClick={() => setActiveTab('auto')}
            style={{
              padding: '10px 20px',
              background: activeTab === 'auto' ? 'var(--primary)' : 'transparent',
              color: activeTab === 'auto' ? 'white' : 'var(--text-secondary)',
              border: 'none',
              cursor: 'pointer',
              borderRadius: '8px',
              fontWeight: '600',
              fontSize: '14px',
            }}
          >
            ⚡ {t('broadcast.tab_auto')}
          </button>
        </div>

        {/* Ручная рассылка */}
        {activeTab === 'manual' && (
          <>
            {error && <div className="message-error">{error}</div>}
            {success && (
              <div className="message-success">
                <strong>{t('broadcast.success_title')}</strong>
                <p>{t('broadcast.success_total')}: {success.total}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} style={{
              background: 'var(--surface-muted)',
              padding: '24px',
              borderRadius: '16px',
              border: '1px solid var(--glass-border)'
            }}>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label>{t('broadcast.broadcast_type')}</label>
                <select
                  value={broadcastType}
                  onChange={(e) => setBroadcastType(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <option value="email">{t('broadcast.type_email')}</option>
                  <option value="telegram">{t('broadcast.type_telegram')}</option>
                  <option value="both">{t('broadcast.type_both')}</option>
                </select>
              </div>

              {broadcastType !== 'email' && (
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label>{t('broadcast.bot_type')}</label>
                  <select
                    value={botType}
                    onChange={(e) => setBotType(e.target.value)}
                    style={{ width: '100%' }}
                  >
                    <option value="old">{t('broadcast.bot_old')}</option>
                    <option value="new">{t('broadcast.bot_new')}</option>
                  </select>
                </div>
              )}

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label>{t('broadcast.recipients')}</label>
                <select
                  value={recipientType}
                  onChange={(e) => setRecipientType(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <option value="all">{t('broadcast.recipient_all')} ({usersEmails.length})</option>
                  <option value="active">{t('broadcast.recipient_active')}</option>
                  <option value="inactive">{t('broadcast.recipient_inactive')}</option>
                  <option value="custom">{t('broadcast.recipient_custom')}</option>
                </select>
              </div>

              {recipientType === 'custom' && (
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label>{t('broadcast.custom_emails')}</label>
                  <textarea
                    value={customEmails}
                    onChange={(e) => setCustomEmails(e.target.value)}
                    placeholder={t('broadcast.custom_emails_placeholder')}
                    rows={4}
                    style={{ width: '100%' }}
                  />
                </div>
              )}

              {(broadcastType === 'email' || broadcastType === 'both') && (
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label>{t('broadcast.subject')} *</label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    required={broadcastType === 'email' || broadcastType === 'both'}
                    style={{ width: '100%' }}
                  />
                </div>
              )}

              {(broadcastType === 'telegram' || broadcastType === 'both') && (
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label>{t('broadcast.photo')}</label>
                  <input type="file" accept="image/*" onChange={handlePhotoChange} />
                  {photoPreview && (
                    <div style={{ marginTop: '10px' }}>
                      <img src={photoPreview} alt="Preview" style={{ maxWidth: '200px', borderRadius: '8px' }}/>
                      <button type="button" onClick={handleRemovePhoto} style={{ marginLeft: '10px' }}>
                        {t('broadcast.remove_photo')}
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label>{t('broadcast.message')} *</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  rows={8}
                  style={{ width: '100%' }}
                />
              </div>

              {(broadcastType === 'telegram' || broadcastType === 'both') && (
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={pinMessage}
                      onChange={(e) => setPinMessage(e.target.checked)}
                    />
                    {t('broadcast.pin_message')}
                  </label>
                </div>
              )}

              <button type="submit" className="btn" disabled={loading}>
                {loading ? t('broadcast.sending') : t('broadcast.send_button')}
              </button>
            </form>
          </>
        )}

        {/* Автоматические рассылки */}
        {activeTab === 'auto' && (
          <div>
            {loadingAuto ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>⏳ Загрузка...</div>
            ) : (
              <>
                {/* Настройки планировщика */}
                <div style={{ 
                  marginBottom: '24px', 
                  padding: '20px', 
                  background: 'linear-gradient(135deg, #667EEA 0%, #764BA2 100%)', 
                  borderRadius: '16px',
                  color: 'white',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '28px' }}>⏰</span>
                      <div>
                        <h3 style={{ margin: 0, fontSize: '18px' }}>Планировщик рассылок</h3>
                        <p style={{ margin: '4px 0 0 0', opacity: 0.8, fontSize: '13px' }}>
                          Автоматическая отправка в заданное время
                        </p>
                      </div>
                    </div>
                    
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={schedulerSettings.enabled}
                        onChange={(e) => setSchedulerSettings({ ...schedulerSettings, enabled: e.target.checked })}
                        style={{ width: '20px', height: '20px' }}
                      />
                      <span style={{ fontWeight: '600' }}>Включено</span>
                    </label>
                  </div>
                  
                  {schedulerSettings.enabled && (
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600' }}>
                        Часы запуска (через запятую):
                      </label>
                      <input
                        type="text"
                        value={schedulerSettings.hours}
                        onChange={(e) => setSchedulerSettings({ ...schedulerSettings, hours: e.target.value })}
                        placeholder="9,14,19"
                        style={{ 
                          width: '100%', 
                          padding: '12px', 
                          borderRadius: '8px', 
                          border: 'none',
                          fontSize: '16px',
                        }}
                      />
                    </div>
                  )}
                  
                  <button
                    type="button"
                    onClick={handleSaveSchedulerSettings}
                    disabled={savingScheduler}
                    style={{
                      padding: '12px 24px',
                      background: 'white',
                      color: '#667EEA',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    {savingScheduler ? '⏳ Сохранение...' : '💾 Сохранить'}
                  </button>
                </div>

                {/* Карточки сообщений */}
                <h3 style={{ marginBottom: '16px' }}>📝 Автоматические сообщения</h3>
                
                {MESSAGE_CONFIGS.map(config => {
                  const msgData = autoMessages[config.key] || {};
                  const isEnabled = msgData.enabled !== false;
                  
                  return (
                    <div key={config.key} style={{
                      background: 'var(--surface-muted)',
                      borderRadius: '12px',
                      border: '1px solid var(--glass-border)',
                      marginBottom: '16px',
                      opacity: isEnabled ? 1 : 0.6,
                    }}>
                      {/* Header */}
                      <div style={{
                        padding: '16px 20px',
                        borderBottom: isEnabled ? '1px solid var(--glass-border)' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '10px',
                            background: config.iconBg,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '20px',
                          }}>
                            {config.icon}
                          </div>
                          <div>
                            <h4 style={{ margin: 0, fontSize: '15px' }}>{config.title}</h4>
                            <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
                              {config.subtitle}
                            </p>
                          </div>
                        </div>
                        
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={isEnabled}
                            onChange={() => handleToggle(config.key, config)}
                            style={{ width: '18px', height: '18px' }}
                          />
                        </label>
                      </div>

                      {/* Body */}
                      {isEnabled && (
                        <div style={{ padding: '20px' }}>
                          {/* Текст сообщения */}
                          <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px' }}>
                              📝 Текст сообщения
                            </label>
                            <textarea
                              value={msgData.message_text || config.defaultText}
                              onChange={(e) => handleFieldChange(config.key, 'message_text', e.target.value)}
                              rows={4}
                              style={{
                                width: '100%',
                                padding: '12px',
                                borderRadius: '8px',
                                border: '1px solid var(--glass-border)',
                                background: 'var(--bg-primary)',
                                color: 'var(--text-primary)',
                                fontSize: '14px',
                                resize: 'vertical',
                              }}
                            />
                          </div>

                          {/* Выбор бота */}
                          <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px' }}>
                              🤖 Отправлять через бота
                            </label>
                            <select
                              value={msgData.bot_type || 'both'}
                              onChange={(e) => handleFieldChange(config.key, 'bot_type', e.target.value)}
                              style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '8px',
                                border: '1px solid var(--glass-border)',
                                background: 'var(--bg-primary)',
                                color: 'var(--text-primary)',
                              }}
                            >
                              <option value="old">{t('broadcast.bot_old')}</option>
                              <option value="new">{t('broadcast.bot_new')}</option>
                              <option value="both">{t('broadcast.bot_both')}</option>
                            </select>
                          </div>

                          {/* Кнопка */}
                          <div style={{
                            background: 'var(--bg-primary)',
                            borderRadius: '10px',
                            padding: '16px',
                            border: '1px dashed var(--glass-border)',
                          }}>
                            <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600', fontSize: '13px' }}>
                              🔘 Кнопка под сообщением
                            </label>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                              <div>
                                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                                  Текст кнопки
                                </label>
                                <input
                                  type="text"
                                  value={msgData.button_text || config.defaultButton?.text || ''}
                                  onChange={(e) => handleFieldChange(config.key, 'button_text', e.target.value)}
                                  placeholder="🛒 Тарифы"
                                  style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '8px',
                                    border: '1px solid var(--glass-border)',
                                    background: 'var(--surface-muted)',
                                    color: 'var(--text-primary)',
                                  }}
                                />
                              </div>
                              <div>
                                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                                  Тип кнопки
                                </label>
                                <select
                                  value={msgData.button_action || config.defaultButton?.action || 'tariffs'}
                                  onChange={(e) => handleFieldChange(config.key, 'button_action', e.target.value)}
                                  style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '8px',
                                    border: '1px solid var(--glass-border)',
                                    background: 'var(--surface-muted)',
                                    color: 'var(--text-primary)',
                                  }}
                                >
                                  <option value="tariffs">🛒 Тарифы (в боте)</option>
                                  <option value="trial">🆓 Активировать триал</option>
                                  <option value="webapp">📱 Mini App</option>
                                  <option value="url">🔗 Внешняя ссылка</option>
                                </select>
                              </div>
                            </div>

                            {(msgData.button_action === 'webapp' || msgData.button_action === 'url') && (
                              <div>
                                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                                  URL
                                </label>
                                <input
                                  type="text"
                                  value={msgData.button_url || ''}
                                  onChange={(e) => handleFieldChange(config.key, 'button_url', e.target.value)}
                                  placeholder="https://..."
                                  style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '8px',
                                    border: '1px solid var(--glass-border)',
                                    background: 'var(--surface-muted)',
                                    color: 'var(--text-primary)',
                                  }}
                                />
                              </div>
                            )}

                            {msgData.button_action === 'tariffs' && (
                              <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
                                ℹ️ Откроет меню тарифов в боте
                              </p>
                            )}
                            {msgData.button_action === 'trial' && (
                              <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
                                ℹ️ Активирует пробный период
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Кнопка сохранения */}
                <button
                  type="button"
                  onClick={handleSaveAutoMessages}
                  disabled={savingAuto}
                  className="btn"
                  style={{
                    width: '100%',
                    padding: '16px',
                    marginTop: '16px',
                    fontSize: '16px',
                  }}
                >
                  {savingAuto ? '⏳ Сохранение...' : '💾 Сохранить все сообщения'}
                </button>
              </>
            )}
          </div>
        )}
      </section>
    </main>
  );
}

export default AdminBroadcastPage;
